import type { DownloadManagerJob } from '@/lib/download-manager-state';

/**
 * Downloads that fail in this browser (a single workspace file) are shown as
 * failed rows in the download manager, never as toasts (#6059). They live
 * only in this tab; server jobs come from the download feed.
 */
const LOCAL_JOB_PREFIX = 'local-download:';

export type LocalDownloadFailure = {
  job: DownloadManagerJob;
  retry: (() => Promise<void>) | null;
  /**
   * The attempt that owns the row (a conversion's Preparing row and the
   * failure that replaces it, or a file download's failure). Tokens grow
   * with each attempt and every menu shares the row, so an older attempt's
   * late result never touches a newer attempt's row, while a newer attempt
   * may replace or clear an older one's (#6171 reviews).
   */
  token: number | null;
};

export type LocalDownloadFailureReport = {
  /** Identifies the file; a second failure of it replaces the first row. */
  key: string;
  fileName: string;
  /** Localized, shown under the file name. */
  reason: string;
  /** Null when another try cannot help. */
  retry: (() => Promise<void>) | null;
  /** Applies only while the row is this attempt's, older, or gone. */
  token?: number | null;
};

export function isLocalDownloadJob(job: Pick<DownloadManagerJob, 'id'>) {
  return job.id.startsWith(LOCAL_JOB_PREFIX);
}

/** Whether an attempt with `token` may change row `id`: newer wins. */
function ownsRow(
  failures: ReadonlyArray<LocalDownloadFailure>,
  id: string,
  token: number | null | undefined
): boolean {
  if (token === null || token === undefined) return true;
  const row = failures.find((failure) => failure.job.id === id);
  return !row || row.token === null || row.token <= token;
}

export function addLocalDownloadFailure(
  failures: ReadonlyArray<LocalDownloadFailure>,
  report: LocalDownloadFailureReport,
  now: number
): LocalDownloadFailure[] {
  const id = `${LOCAL_JOB_PREFIX}${report.key}`;
  if (!ownsRow(failures, id, report.token)) return [...failures];
  return [
    {
      job: {
        id,
        archiveName: report.fileName,
        kind: 'direct_asset',
        deliveryMode: 'direct_source',
        status: 'failed',
        progress: 0,
        itemCount: 0,
        completedItemCount: 0,
        createdAt: now,
        reasonText: report.reason,
        retryable: report.retry !== null,
      },
      retry: report.retry,
      token: report.token ?? null,
    },
    ...failures.filter((failure) => failure.job.id !== id),
  ];
}

/**
 * A download being prepared in this browser (a conversion): a running row,
 * so its progress is in the panel, with no loading toasts (TASTE #6171).
 * A failure with the same key replaces it; success removes it.
 */
export function addLocalDownloadPreparing(
  failures: ReadonlyArray<LocalDownloadFailure>,
  report: { key: string; fileName: string; token: number },
  now: number
): LocalDownloadFailure[] {
  const id = `${LOCAL_JOB_PREFIX}${report.key}`;
  return [
    {
      job: {
        id,
        archiveName: report.fileName,
        kind: 'direct_asset',
        deliveryMode: 'direct_source',
        status: 'running',
        progress: 0,
        itemCount: 0,
        completedItemCount: 0,
        createdAt: now,
      },
      retry: null,
      token: report.token,
    },
    ...failures.filter((failure) => failure.job.id !== id),
  ];
}

export function localDownloadJobId(key: string): string {
  return `${LOCAL_JOB_PREFIX}${key}`;
}

/** Removes a row; with a token, only a row of that attempt or an older one. */
export function removeLocalDownloadFailure(
  failures: ReadonlyArray<LocalDownloadFailure>,
  jobId: string,
  token?: number | null
): LocalDownloadFailure[] {
  if (!ownsRow(failures, jobId, token)) return [...failures];
  return failures.filter((failure) => failure.job.id !== jobId);
}

/**
 * Runs a failed row's Retry at most once at a time per row: a double click
 * (before the row re-renders) must not start the download twice.
 */
export function createLocalRetryGate() {
  const inFlight = new Set<string>();
  return async (jobId: string, run: () => Promise<void>): Promise<boolean> => {
    if (inFlight.has(jobId)) return false;
    inFlight.add(jobId);
    try {
      await run();
      return true;
    } finally {
      inFlight.delete(jobId);
    }
  };
}

/**
 * The attempt tokens of rows the member closed. A late result from one of
 * those attempts is dropped, so a closed row doesn't come back (#6171).
 */
export function closedLocalDownloadTokens(
  failures: ReadonlyArray<LocalDownloadFailure>
): number[] {
  return failures.flatMap((failure) =>
    failure.token === null ? [] : [failure.token]
  );
}

export function isClosedLocalDownloadReport(
  closed: ReadonlySet<number>,
  report: Pick<LocalDownloadFailureReport, 'token'>
): boolean {
  return typeof report.token === 'number' && closed.has(report.token);
}

/**
 * Per row (file, or file and format), the newest attempt token seen. It
 * outlives the row, so once a newer attempt has cleared or replaced it, an
 * older attempt's late failure can't bring a row back (#6171 review).
 */
export function createLocalDownloadHighWater() {
  const marks = new Map<string, number>();
  return {
    note(key: string, token: number | null | undefined) {
      if (typeof token !== 'number') return;
      marks.set(key, Math.max(marks.get(key) ?? 0, token));
    },
    /** True for a report from an attempt older than one already seen. */
    isStale(key: string, token: number | null | undefined) {
      return typeof token === 'number' && token < (marks.get(key) ?? 0);
    },
  };
}
